import * as React from 'react';
import { connect } from 'react-redux';
import { debounce } from 'lodash';
import { Link } from 'react-router-dom';
import {
  Form,
  CompanyList,
  Box,
  CompanyAvatar,
  Pagination,
  Icon,
  IconButton,
} from '..';
import { getCompanies, getCompany } from '../../redux/actions/companies';
import Loader from '../Loader/Loader';
import PAGINATE from '../../data/paginate';

interface Props {
  companyId?: number;
  handleUpdate: (companyId: number) => void;
  dispatch: Function;
  companies: any[];
}

interface State {
  search: string;
  page: number;
  company: any;
  isReady: boolean;
  isSearching: boolean;
  isLoading: boolean;
}

class CompanyPicker extends React.Component<Props, State> {
  constructor(props) {
    super(props);
    this.state = {
      search: '',
      page: 0,
      company: undefined,
      isReady: false,
      isSearching: false,
      isLoading: false,
    };
    this.setCompany = this.setCompany.bind(this);
    this.clearCompany = this.clearCompany.bind(this);
  }

  async componentDidMount() {
    if (this.props.companyId) {
      await this.setCompany(this.props.companyId, true);
    }
    this.setState({ isReady: true });
  }

  async setCompany(id: number, initialLoad = false) {
    const response = await this.props.dispatch(getCompany(id));
    if (response.data) {
      this.setState({
        company: response.data,
        search: '',
        page: 0,
        isSearching: false,
      });
      if (!initialLoad) {
        this.props.handleUpdate(id);
      }
    }
  }

  async clearCompany() {
    if (window.confirm('Remove this company from this driver?')) {
      this.setState({ company: undefined });
      this.props.handleUpdate(null);
    }
  }

  render() {
    if (!this.state.isReady) {
      return <Loader fullwidth />;
    }
    return (
      <>
        <div className="field" />
        <div className="field">
          <label className="label">Company</label>
          {!!this.state.company ? (
            <Box key={this.state.company.id}>
              <div className="media">
                <Link
                  className="media-left"
                  to={`/companies/${this.state.company.id}`}
                >
                  <CompanyAvatar company={this.state.company} />
                </Link>
                <div className="media-content">
                  <strong>{this.state.company.name}</strong>
                  {!!this.state.company.phone_number && (
                    <>
                      <br />
                      <small>
                        <Icon icon="phone has-text-grey-light" />
                        &nbsp;
                        {this.state.company.phone_number}
                      </small>
                    </>
                  )}
                  {!!this.state.company.city && (
                    <>
                      <br />
                      <small>
                        <Icon icon="map-pin has-text-grey-light" />
                        &nbsp;
                        {this.state.company.city}
                      </small>
                    </>
                  )}
                </div>
                <div className="media-right">
                  <a
                    className="delete"
                    onClick={() => {
                      this.clearCompany();
                    }}
                  />
                </div>
              </div>
            </Box>
          ) : (
            <span className="has-text-grey">N/A</span>
          )}
        </div>
        {!this.state.isSearching ? (
          <div className="buttons is-right">
            <IconButton
              className="is-default"
              icon="search"
              handleClick={() => {
                this.setState(
                  { search: '', page: 0, isSearching: true, isLoading: true },
                  async () => {
                    await this.props.dispatch(getCompanies());
                    this.setState({ isLoading: false });
                  },
                );
              }}
            >
              Find a Company
            </IconButton>
          </div>
        ) : (
          <Box>
            <a
              className="delete is-pulled-right"
              onClick={() => this.setState({ isSearching: false })}
            />
            <div className="field">
              <label className="label">Find a Company</label>
              <Form
                type="search"
                fields={[{ name: 'search', type: 'text' }]}
                hasButton={false}
                handleChange={debounce(({ search }) => {
                  this.setState(
                    { search, page: 0, isLoading: true },
                    async () => {
                      await this.props.dispatch(
                        getCompanies(undefined, 0, search),
                      );
                      this.setState({ isLoading: false });
                    },
                  );
                }, 200)}
              />
            </div>
            {this.state.isLoading ? (
              <Loader fullwidth />
            ) : (
              <>
                <div className="field">
                  <CompanyList
                    hideSearch
                    handleClick={(company) => {
                      this.setCompany(Number(company.id));
                    }}
                    items={this.props.companies.slice(
                      0,
                      PAGINATE.companies.MAX,
                    )}
                  />
                </div>
                {this.props.companies.length && (
                  <div className="field">
                    <Pagination
                      page={this.state.page}
                      lastPage={
                        this.props.companies.length <= PAGINATE.companies.MAX
                      }
                      handleUpdate={async (page) => {
                        this.setState({ page });
                        await this.props.dispatch(
                          getCompanies(undefined, page, this.state.search),
                        );
                      }}
                    />
                  </div>
                )}
              </>
            )}
          </Box>
        )}
      </>
    );
  }
}

const mapStateToProps = ({ companies }) => ({
  companies: companies.companies,
});

export default connect(mapStateToProps)(CompanyPicker);
